import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import {
  UserSwitchOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  MessageOutlined,
  UndoOutlined,
  MessageFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TableMainContent from 'src/components/table-main-content';
import SimpleTableContent from 'src/components/simple-table-content';
import { noop, get } from 'lodash';
import LanguageFlagList from 'src/components/language-flag-list';
import { Divider, Table, Tooltip, Checkbox, Tag } from 'antd';
import useTableHoverRow from 'src/hooks/use-table-hover-row';
import './style.scss';
import CollaboratorComments from './collaboratorComments';
import { FitsDetails } from 'src/components/fits-details';


function DeallocationTable({
  data,
  user,
  canMatch,
  canFit,
  canFitPeople,
  canDelete,
  onClickDelete,
  onClickEdit,
  onClickFit,
  onClickMatch,
  onClickComment,
  onClickCancel,
  onClickInactivate,
  onClickReactivate,
  pagination,
  onPaginationChange,
  loading
}) {
  const [expandedRows, setExpandedRows] = useState([]);
  const { key: hoverKey, onMove, onLeave } = useTableHoverRow();
  const { t } = useTranslation();
  
  const paginationConfig = pagination;
  
  const handleKeyDown = (event) => {
    if (event.key === 'F1') {
      const actionsDivs = Array.from(
        document.querySelectorAll('[id^="actions"]')
      );
      const currentIndex = actionsDivs.findIndex(
        (div) => div === document.activeElement
      );
      const nextIndex = currentIndex + 1;
      const nextActionsDiv = actionsDivs[nextIndex];
      if (nextActionsDiv) {
        nextActionsDiv.scrollIntoView({ behavior: 'smooth' });
        nextActionsDiv.focus();
      } else if (currentIndex === actionsDivs.length - 1) {
        actionsDivs[0].scrollIntoView({ behavior: 'smooth' });
        actionsDivs[0].focus();
      }
    }
  };
  
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  const columnsConfig = [
    {
      title: 'Login',
      width: 150,
      dataIndex: 'collaborator',
      fixed: 'left',
      sorter: (a, b) => {
        const nameA = get(a.collaborator_deallocation, 'login', '-');
        const nameB = get(b.collaborator_deallocation, 'login', '-');
        return nameA.localeCompare(nameB);
      },
      render: (text, record) => {
        const { collaborator_deallocation } = record;
        const userLoginBase = user?.login?.includes('@') ? user.login.split('@')[0] : user?.login;

        const collaboratorPdm = collaborator_deallocation?.pdm?.includes('@')
          ? collaborator_deallocation.pdm.split('@')[0]
          : collaborator_deallocation?.pdm;

        const isCollaboratorPDM = userLoginBase && collaboratorPdm &&
          userLoginBase.toLowerCase().trim() === collaboratorPdm.toLowerCase().trim();
        const isFitFree = !record.needs || record.needs.length === 0;        
        const tooltipText = t('tooltip.disableFit');

        const isInactivated = record.inactivationReason;
        const isBP = user?.profile === 'BP';
        const canInactivate = (isCollaboratorPDM || isBP) && isFitFree && !isInactivated;
        const canReactivate = (isCollaboratorPDM || isBP) && isInactivated;

        const handleInactivateClick = () => {
          if (canInactivate) {
            onClickInactivate(record);
          } else if (canReactivate && onClickReactivate) {
            onClickReactivate(record);
          }
        };

        return (
          <div className='profile-table__cell'>
            <TableMainContent
              mainContent={collaborator_deallocation.login}
              mainContentAriaLabel={collaborator_deallocation.login}
              translationMainContent={t('collaboratorLogin')}
              subContent={
                collaborator_deallocation.name !== 'Not Found'
                  ? collaborator_deallocation.name
                  : t('hasNoInformation')
              }
              subContentAriaLabel={
                collaborator_deallocation.name !== 'Not Found'
                  ? collaborator_deallocation.name
                  : t('hasNoInformation')
              }
              translationSubContent={t('name')}
              upperContent={
                collaborator_deallocation.isHappyFeet ? 'Happy Feet' : null
              }
              bottomContent={
              expandedRows.includes(record.id) ? (
                isInactivated ? (
                  <p className="inactivation-reason-inline" style={{ opacity: 0.7 }}>
                    <b>{t('inactivatedDueTo')}</b> {record.inactivationReason}
                  </p>
                ) : null
              ) : (
                (isCollaboratorPDM || isBP) ? (
                  isFitFree || isInactivated ? (
                    <div
                      className="inactivate-button"
                      onClick={handleInactivateClick}
                      role="button"
                      tabIndex={0}
                      aria-label={isInactivated ? t('reactivateProfile') : t('inactivateProfile')}
                      title={isInactivated ? `Motivo: ${record.inactivationReason}` : ''}
                    >
                      <Checkbox
                        checked={isInactivated}
                        disabled={false}
                        className="inactivate-checkbox"
                      >
                        <span className="inactivate-text">{t('inactivate')}</span>
                      </Checkbox>
                    </div>
                  ) : (
                    <Tooltip placement="top" title={t('tooltip.disableFit')} overlayClassName="tooltip-inactivate">
                      <Checkbox
                        aria-label={t('inactivateProfile')}
                        id={`inactivate-${collaborator_deallocation.id}`}
                        disabled={true}
                        checked={isInactivated}
                        className="inactivate-checkbox"
                      >
                        <span className="inactivate-text">{t('inactivate')}</span>
                      </Checkbox>
                    </Tooltip>
                  )
                ) : null
              )
            }
            />
          </div>
        );
      },
    },
    {
      title: t('role'),
      dataIndex: 'papel',
      width: 120,
      sorter: (a, b) => {
        const roleA = get(a.collaborator_deallocation, 'role', '-');
        const roleB = get(b.collaborator_deallocation, 'role', '-');
        return roleA.localeCompare(roleB);
      },
      render: (text, record) => {
        return (
          <>
            <TableMainContent
              mainContent={record.collaborator_deallocation.role}
              mainContentAriaLabel={record.collaborator_deallocation.role}
              translationMainContent={t('role')}
              subContent={record.collaborator_deallocation.positionMap}
              subContentAriaLabel={record.collaborator_deallocation.positionMap}
              translationSubContent={t('positionMap')}
            />
          </>
        );
      },
    },
    {
      title: t('tech'),
      dataIndex: 'technology',
      width: 150,
      sorter: (a, b) => {
        const techA = a.collaborator_deallocation.technologies[0]?.skill ?? '-';
        const techB = b.collaborator_deallocation.technologies[0]?.skill ?? '-';
        if (techA === techB) {
          return 0;
        } else if (techA === '-') {
          return 1;
        } else if (techB === '-') {
          return -1;
        } else {
          return techA.localeCompare(techB);
        }
      },
      render: (text, record) => {
        const englishLevel = record.collaborator_deallocation.englishLevel;
        const mainSkills = record.collaborator_deallocation.technologies.filter(
          (collaborator) => collaborator.details.mainSkill === 'Y'
        );
        let mainSkillName = '-';
        let mainSkillRating = '-';
        if (mainSkills.length > 0) {
          mainSkills.map((tech, key) => {
            mainSkillName = tech.skill ?? '-';
            mainSkillRating = tech.details?.rating ?? '-';
          });
        }
        return (
          <>
            <TableMainContent
              mainContent={mainSkillName}
              mainContentAriaLabel={
                mainSkillName === '-' ? t('hasNoInformation') : mainSkillName
              }
              translationMainContent={t('mainSkill')}
              subContent={`${t('level')} ${mainSkillRating}`}
              subContentAriaLabel={
                mainSkillRating === '-'
                  ? t('hasNoInformation')
                  : mainSkillRating
              }
              translationSubContent={t('level')}
            />
            <LanguageFlagList englishLevel={englishLevel} />
          </>
        );
      },
    },
    {
      title: 'GU',
      dataIndex: 'gu',
      width: 120,
      sorter: (a, b) => {
        const localA = get(a.collaborator_deallocation, 'gu', '-');
        const localB = get(b.collaborator_deallocation, 'gu', '-');
        return localA.localeCompare(localB);
      },
      render: (text, { collaborator_deallocation }) => (
        <TableMainContent
          mainContent={collaborator_deallocation?.gu ?? '-'}
          mainContentAriaLabel={
            collaborator_deallocation?.gu ?? t('hasNoInformation')
          }
          translationMainContent={t('growthUnit')}
          subContent={collaborator_deallocation?.alliance ?? '-'}
          subContentAriaLabel={
            collaborator_deallocation?.alliance ?? t('hasNoInformation')
          }
          translationSubContent={t('alliance')}
        />
      ),
    },
    {
      title: t('availability'),
      dataIndex: 'date',
      width: 160,
      sorter: (a, b) => {
        const monthA = get(a.collaborator_deallocation, 'numberMonths', 0);
        const monthB = get(b.collaborator_deallocation, 'numberMonths', 0);
        return monthB - monthA;
      },
      render: (text, record) => {
        const { collaborator_deallocation } = record;
        const numberMonths = collaborator_deallocation?.numberMonths;
        const isSingular =
          numberMonths === 1 || numberMonths === -1 ? t('month') : t('months');
        const dateRef = new Date(collaborator_deallocation?.cutoffDateRef);
        const formattedDate = `${dateRef.getFullYear()}-${(
          dateRef.getMonth() + 1
        )
          .toString()
          .padStart(2, '0')}`;
        const changeValueofMonths = () => {
          let labels = [];
          if (numberMonths >= 1) {
            labels.push(`${t('availableIn')} ${formattedDate}`);
            labels.push(
              `${Math.abs(numberMonths)} ${isSingular} ${t('ahead')}`
            );
          } else {
            labels.push(`${t('availableSince')} ${formattedDate}`);
            labels.push(`${Math.abs(numberMonths)} ${isSingular} ${t('ago')}`);
          }
          if (
            new Date().getMonth() ===
            new Date(collaborator_deallocation.cutoffDateRef).getMonth()
          ) {
            labels.pop();
            labels.push(t('isAvailableNow'));
          }
          return labels;
        };
        const hasAvailability =
          numberMonths === null || numberMonths === undefined
            ? '-'
            : changeValueofMonths();
        return (
          <>
            <TableMainContent
              mainContentClass={
                record.inactivationReason
                  ? null
                  : numberMonths > 0 ? 'main-content-green' : 'main-content-red'
              }
              mainContent={hasAvailability[0] ?? '-'}
              mainContentAriaLabel={
                hasAvailability[0] === '-'
                  ? t('hasNoInformation')
                  : hasAvailability[0]
              }
              translationMainContent={t('availability')}
              subContent={hasAvailability[1] ?? '-'}
              subContentAriaLabel={hasAvailability[1] ?? t('hasNoInformation')}
              translationSubContent=''
            />
          </>
        );
      },
    },
    {
      title: 'PDM',
      dataIndex: 'pdm',
      width: 120,
      sorter: (a, b) => {
        const nameA = a.collaborator_deallocation.pdm;
        const nameB = b.collaborator_deallocation.pdm;
        return nameA.localeCompare(nameB);
      },
      render: (text, { collaborator_deallocation }) => (
        <SimpleTableContent
          mainContent={collaborator_deallocation.pdm ?? '-'}
          mainContentAriaLabel={
            collaborator_deallocation.pdm ?? t('hasNoInformation')
          }
          translationMainContent={t('pdm')}
        />
      ),
    },
    {
      title: t('fitStatus'),
      dataIndex: 'status',
      width: 130,
      defaultSortOrder: 'ascend',
      sorter: (reg1, reg2) => {
        if (reg1.match && reg2.match) {
          return 0;
        }
        if (reg1.match) return -1;
        if (reg2.match) return 1;
        const reg1Needs = reg1.needs.length;
        const reg2Needs = reg2.needs.length;
        if (reg1Needs === 1 && reg2Needs === 1) return 0;
        if (reg1Needs === 1) return -1;
        if (reg2Needs === 1) return 1;
        if (reg1Needs > 0 && reg2Needs > 0) return 0;
        if (reg1Needs > 0) return -1;
        if (reg2Needs > 0) return 1;
        return 0;
      },
      render: (_, deallocation) => {
        const isFree = () => ['Free', 'Disponível'].includes(deallocation.statusFit);
        const activeFits = deallocation.needs.filter((need) => need.fits.active === 'Y' && need.fits.match === 'N');
        const fitCount = activeFits.length;

        if (isFree()) {
          return (
            <SimpleTableContent
              mainContent={deallocation.statusFit}
              mainContentAriaLabel={deallocation.statusFit}
              translationMainContent={t('fitStatus')}
              mainContentClass={
                deallocation.inactivationReason
                  ? 'inactivated'
                  : 'pending'
              }
            />
          );
        }

        const matchNeed = deallocation.needs.find((n) => n.fits.match === 'Y');
        const subContent = matchNeed
          ? `${t('withProject')} ${matchNeed.title}\n#${matchNeed.requisitionCode}`
          : undefined;

        return (
          <TableMainContent
            mainContent={deallocation.statusFit}
            mainContentAriaLabel={deallocation.statusFit}
            translationMainContent={t('fitStatus')}
            mainContentClass={
              deallocation.statusFit === 'Fit' || deallocation.statusFit === 'Match'
                ? 'match'
                : 'pending'
            }
            subContent={subContent}
            subContentAriaLabel={subContent}
            translationSubContent={subContent ? t('fitStatusProject', { deallocation }) : undefined}
            fitDetails={
              deallocation.statusFit === 'Fit' && fitCount > 0
                ? { fitCount }
                : undefined
            }
          />
        );
      },
    },
    {
      title: t('actions'),
      dataIndex: '',
      fixed: 'right',
      width: 140,
      className: 'profile-table__actions',
      tabIndex: 0,
      render: (text, deallocation) => {
        const { needs = [] } = deallocation;
        const activeNeeds = needs.filter(
          (need) => need.active === 'Y' && need.fits.active === 'Y' && need.fits.match === 'N'
        );
        const matchNeedId = activeNeeds[0]?.id 
          ?? needs.find((need) => need.fits.match === 'Y' && need.fits.active === 'Y')?.id;
        const allowedEmailsToMatch = needs
          .map((need) => need.requisitionTalentAttractionEmail)
          .concat(needs.map((need) => need.requisitionManagerEmail));
        const userIsResponsibleForAFit = activeNeeds.some(
          (need) => need.fits.responsibleId === user.id
        );
        const canUserMatch =
          user.profile === 'ADM' ||
          (canMatch && allowedEmailsToMatch.includes(user.email) && userIsResponsibleForAFit);
        const isInactivated = deallocation.inactivationReason;
        return (
          <div
            id='actions'
            onKeyDown={handleKeyDown}
            tabIndex={0}
            aria-label={t('actionsGroup', {
              login: deallocation.collaborator_deallocation.login,
            })}
            role='group'
            className='profile-table__actions_group'
          >
            {canUserMatch && deallocation.match && (
              <Tooltip placement='topRight' title={t('cancelMatch')}>
                <UndoOutlined
                  style={{ fontSize: 20, color: '#4a4a4a' }}
                  onClick={() =>
                    onClickCancel({
                      deallocationId: deallocation.id,
                      matchNeedId: matchNeedId,
                    })
                  }
                  twoToneColor
                  aria-label={t('rollbackMatch')}
                  tabIndex={0}
                  role='button'
                  type='button'
                  id='cancel-match'
                  title={t('rollbackMatch')}
                />
              </Tooltip>
            )}
            {!deallocation.match && (
              <>
                {canUserMatch && activeNeeds.length > 0 && (
                  <Tooltip placement='topRight' title={t('confirmMatch')}>
                    <CheckCircleOutlined
                      style={{ fontSize: 20, color: '#27AE60' }}
                      onClick={() => onClickMatch(deallocation)}
                      twoToneColor
                      aria-label={t('openMatchOptions')}
                      tabIndex={0}
                      role='button'
                      type='button'
                      id='confirm-match'
                      title={t('openMatchOptions')}
                    />
                  </Tooltip>
                )}
                {(canFit || canFitPeople) ? (
                  <Tooltip
                    placement='topRight'
                    title={isInactivated ? t('profileInactivated') : t('requestViewFits')}
                  >
                    <UserSwitchOutlined
                      style={{
                        fontSize: 20,
                        color: isInactivated ? '#bfbfbf' : '#4a4a4a',
                        ...(isInactivated ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}),
                      }}
                      onClick={isInactivated ? undefined : () => onClickFit(deallocation)}
                      aria-label={t('openFitOptions')}
                      aria-disabled={isInactivated}
                      tabIndex={0}
                      role='button'
                      type='button'
                      id='request-view-fits'
                      title={t('openFitOptions')}
                    />
                  </Tooltip>
                ) : null}
                {canDelete &&
                  activeNeeds.length <= 0 &&
                  deallocation.collaborator_deallocation.isImported === 'N' && (
                    <Tooltip placement='topRight' title={t('edit')}>
                      <EditOutlined
                        style={{ fontSize: 20, color: '#4a4a4a' }}
                        onClick={() => onClickEdit(deallocation)}
                        aria-label={t('editProfile')}
                        tabIndex={0}
                        role='button'
                        type='button'
                        id='edit-profile'
                        title={t('editProfile')}
                      />
                    </Tooltip>
                  )}
                <Tooltip placement='topRight' title={t('comment')}>
                  {deallocation?.hasComments ? (
                    <MessageFilled
                      style={{ fontSize: 20, color: '#000050' }}
                      onClick={() => onClickComment(deallocation)}
                      aria-label={t('commentProfile')}
                      tabIndex={0}
                      role='button'
                      type='button'
                      id='comment-profile'
                      title={t('commentProfile')}
                    />
                  ) : (
                    <MessageOutlined
                      style={{ fontSize: 20, color: '#4a4a4a' }}
                      onClick={() => onClickComment(deallocation)}
                      aria-label={t('commentProfile')}
                      tabIndex={0}
                      role='button'
                      type='button'
                      id='comment-profile'
                      title={t('commentProfile')}
                    />
                  )}
                </Tooltip>
                {canDelete &&
                  activeNeeds.length <= 0 &&
                  deallocation.collaborator_deallocation.isImported === 'N' && (
                    <Tooltip placement='topRight' title={t('delete')}>
                      <DeleteOutlined
                        style={{ fontSize: 20, color: '4a4a4a' }}
                        onClick={() => onClickDelete(deallocation)}
                        aria-label={t('deleteProfile')}
                        tabIndex={0}
                        role='button'
                        type='button'
                        id='delete-profile'
                        title={t('deleteProfile')}
                      />
                    </Tooltip>
                  )}
              </>
            )}
          </div>
        );
      },
    },
  ];
  
  const handleTableChange = (paginationConfig, filters, sorter) => {
    if (onPaginationChange) {
      onPaginationChange(paginationConfig.current, paginationConfig.pageSize);
    }
  };
  
  return (
    <div className='profile-table__content'>
      <Table
        scroll={{
          x: 800,
        }}
        rowKey='id'
        onRow={(item, index) => {
          const userLoginBase = user?.login?.includes('@') ? user.login.split('@')[0] : user?.login;
          const collaboratorPdm = item.collaborator_deallocation?.pdm?.includes('@')
            ? item.collaborator_deallocation.pdm.split('@')[0]
            : item.collaborator_deallocation?.pdm;
          const isCollaboratorPDM = userLoginBase && collaboratorPdm &&
            userLoginBase.toLowerCase().trim() === collaboratorPdm.toLowerCase().trim();
          const isBP = user?.profile === 'BP';

          return {
            onMouseLeave: () => onLeave(),
            onMouseMove: () => onMove(item.id),
            className: classnames({
              'row-even': index % 2 === 0,
              'row-odd': index % 2 !== 0,
              'row-inactivated': item.inactivationReason,
              'row-inactivated-no-checkbox': item.inactivationReason && !isCollaboratorPDM && !isBP
            }),
          };
        }}
        expandable={{
          expandedRowRender: (record) => {
            const collab = record.collaborator_deallocation;
            const isInactivated = record.inactivationReason;
            
            return (
              <div
                aria-label={t('moreInfoProfile', { login: collab.login })}
                id='description'
                role='group'
                style={isInactivated ? { opacity: 0.7 } : undefined}
              >
                <div
                  className='profile-table__content_description'
                  tabIndex={0}
                  aria-labelledby='description'
                >
                  <div
                    onMouseMove={() => onMove(record.id)}
                    onMouseLeave={onLeave}
                  >
                    {collab.site && (
                      <p>
                        <b>{t('site')}:</b> {collab.site}
                      </p>
                    )}
                    {collab.isHomeOffice && (
                      <p>
                        <b>{t('contract.label')}:</b>{' '}
                        {t(
                          `contract.${
                            collab.isHomeOffice === 'Y' ? 'remote' : 'onSite'
                          }`
                        )}
                      </p>
                    )}
                    {collab.businessPartner && (
                      <p>
                        <b>Business Partner:</b> {collab.businessPartner}
                      </p>
                    )}
                    {collab.contractType && (
                      <p>
                        <b>{t('contractType')}:</b> {collab.contractType}
                      </p>
                    )}
                    {collab.personWithDisability && (
                      <p>
                        <b>{t('pwd')}:</b> {collab.personWithDisability}
                      </p>
                    )}
                    {collab.client && (
                      <p>
                        <b>{t('currentClient')}:</b> {collab.client}
                      </p>
                    )}
                    {collab.cLob && (
                      <p>
                        <b>C-lob:</b> {collab.cLob}
                      </p>
                    )}
                    <Divider />
                    {record.description && (
                      <p>
                        <b>{t('notesOptional')}:</b> {record.description}
                      </p>
                    )}
                  </div>
                  <div
                    onMouseMove={() => onMove(record.id)}
                    onMouseLeave={onLeave}
                  >
                    {collab.technologies && (() => {
                      const degreedSkills = collab.technologies.filter(
                        (tech) => tech.details.source === 'DEGREED'
                      );
                      const journeySkills = collab.technologies.filter(
                        (tech) => tech.details.source === 'JOURNEY' || tech.details.source === 'PDM'
                      );
                      return (
                        <>
                          {degreedSkills.length > 0 && (
                            <div className='skill-section'>
                              <b>{t('topSkillsFromDegreed')}:</b>
                              <div className='skill-tag-list'>
                                {degreedSkills.map((tech) => (
                                  <Tag key={tech.id} className='skill-tag'>
                                    {tech.skill}{tech.details.rating ? ` - ${t('level')} ${tech.details.rating}` : ''}
                                  </Tag>
                                ))}
                              </div>
                            </div>
                          )}
                          {journeySkills.length > 0 && (
                            <div className='skill-section'>
                              <b>{t('topSkillsFromJourney')}:</b>
                              <div className='skill-tag-list'>
                                {journeySkills.map((tech) => (
                                  <Tag key={tech.id} className='skill-tag'>
                                    {tech.skill}
                                  </Tag>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                    <FitsDetails
                      fits={record.needs
                        .filter((need) => need.fits.active === 'Y' && need.fits.match === 'N')
                        .map((need) => ({
                          id: need.fits.id,
                          requisitionCode: need.requisitionCode,
                          fitBy: need.fits.responsibleLogin,
                          expiresAt: need.fits.expiresAt,
                        }))}
                    />
                    <CollaboratorComments
                    user={user}
                    id={collab.id}
                    pdm={collab.pdm}
                    comment={collab.comments}
                    isInactivated={isInactivated}
                  />
                </div>
              </div>
            );
          },
          expandIcon: ({ expanded, onExpand, record }) => {
            const collab = record.collaborator_deallocation;
            return expanded ? (
              <button
                type='button'
                className='ant-table-row-expand-icon ant-table-row-expand-icon-expanded'
                aria-label={t('collapseRow', { collapsedValue: collab.login })}
                onClick={(e) => onExpand(record, e)}
                tabIndex={0}
              />
            ) : (
              <button
                type='button'
                className='ant-table-row-expand-icon ant-table-row-expand-icon-collapsed'
                aria-label={t('expandRow', { expandedValue: collab.login })}
                onClick={(e) => onExpand(record, e)}
                tabIndex={0}
              />
            );
          },
          expandedRowClassName: (record) => {
            const index = data.findIndex((item) => item.id === record.id);
            const userLoginBase = user?.login?.includes('@') ? user.login.split('@')[0] : user?.login;
            const collaboratorPdm = record.collaborator_deallocation?.pdm?.includes('@')
              ? record.collaborator_deallocation.pdm.split('@')[0]
              : record.collaborator_deallocation?.pdm;
            const isCollaboratorPDM = userLoginBase && collaboratorPdm &&
              userLoginBase.toLowerCase().trim() === collaboratorPdm.toLowerCase().trim();
            const isBP = user?.profile === 'BP';
            return classnames({
              'is-matched': record.match === 'Y',
              'row-odd': index % 2 !== 0,
              'row-even': index % 2 === 0,
              'row-inactivated': record.inactivationReason,
              'row-inactivated-no-checkbox': record.inactivationReason && !isCollaboratorPDM && !isBP
            });
          },
          onExpandedRowsChange: (expandedRows) => {
            setExpandedRows(expandedRows);
          },
          key: 'id',
        }}
        pagination={paginationConfig}
        loading={loading}
        onChange={handleTableChange}
        rowClassName={(record, index) =>
          classnames({
            'hovered-row': hoverKey === record.id,
            'is-matched': record.match,
            'expanded-row': expandedRows.includes(record.id),
            'row-odd': index % 2 !== 0,
            'row-even': index % 2 === 0,
            'row-inactivated': record.inactivationReason
          })
        }
        columns={columnsConfig}
        dataSource={data}
      />
    </div>
  );
}

DeallocationTable.defaultProps = {
  data: [],
  user: {},
  canMatch: false,
  canFit: false,
  canFitPeople: false,
  canDelete: false,
  onClickDelete: noop,
  onClickEdit: noop,
  onClickFit: noop,
  onClickMatch: noop,
  onClickComment: noop,
  onClickCancel: noop,
  onClickInactivate: noop,
  onClickReactivate: noop,
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100']
  },
  onPaginationChange: noop,
  loading: false
};

export default DeallocationTable;